import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  castBallot,
  closeVote,
  openVote,
  type Objective,
  type SessionFull,
  type VoteView,
} from '../api/missions';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import type { ObjectivePatch } from './objectiveEditing';
import { OBJECTIVE_ICONS } from './objectiveIcons';
import { PlannedChip } from './Planned';
import { errorText } from './useSessionLive';

interface VotesProps {
  code: string;
  votes: VoteView[];
  canDirect: boolean;
  onVotes: (votes: VoteView[]) => void;
  onError: (text: string | null) => void;
  /**
   * The session page's DECIDE card (UI doc 5.1): nothing at all while no vote
   * is open, and the closed ones left to the timeline, where they fold. The
   * direction page keeps both, since it is where votes are opened and closed.
   */
  live?: boolean;
  /** Who could vote, for "12 of 18 voted" when the vote has no quorum: the players on the server. */
  eligible?: number | null;
}

/** Ticks once a second while `active`, for a countdown; frozen (no re-renders) otherwise. */
function useClock(active: boolean) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

/** "3:45" until `closesAt`, or null once it has passed (the poll or the stream will close it soon). */
function countdown(closesAt: string, now: number): string | null {
  const ms = new Date(closesAt).getTime() - now;
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * The DECIDE card (UI doc 5.1): one question, the options as big buttons with the live tally
 * as a thin bar under each, a countdown, my vote highlighted and "12 of 18 voted". One tap
 * votes and another tap changes it while the vote is open. Closed votes collapse into a
 * compact list, or into the timeline on the session page, and directors get the form to
 * open one.
 */
export function VotesPanel({
  code,
  votes,
  canDirect,
  onVotes,
  onError,
  live = false,
  eligible = null,
}: VotesProps) {
  const { me } = useAuth();
  const [question, setQuestion] = useState('');
  const [key, setKey] = useState('free');
  const [options, setOptions] = useState('');
  const [minutes, setMinutes] = useState('');
  const [quorum, setQuorum] = useState('');
  const open = votes.filter((v) => v.state === 'OPEN');
  const closed = votes.filter((v) => v.state !== 'OPEN');
  const now = useClock(open.some((v) => v.closesAt != null));

  const run = (p: Promise<VoteView[]>) =>
    p
      .then((v) => {
        onVotes(v);
        onError(null);
      })
      .catch((e) => onError(errorText(e)));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const list = options
      .split('\n')
      .map((o) => o.trim())
      .filter(Boolean);
    const closesAt = minutes.trim()
      ? new Date(Date.now() + Number(minutes) * 60000).toISOString()
      : null;
    const closesAtBallots = quorum.trim() ? Number(quorum) : null;
    void run(
      openVote(code, {
        key: key.trim() || 'free',
        question,
        options: list,
        closesAt,
        closesAtBallots,
      }),
    ).then(() => {
      setQuestion('');
      setOptions('');
      setMinutes('');
      setQuorum('');
    });
  };

  // A player's page with nothing to decide shows no card at all; the director's
  // page still says so, beside the form that opens one.
  if (live && open.length === 0 && !canDirect) return null;
  return (
    <section className="cards" aria-label={ca.votes}>
      {votes.length === 0 && !live && <p className="muted small">{ca.noVotes}</p>}
      {open.map((v) => {
        const total = Object.values(v.counts).reduce((a, b) => a + b, 0);
        const left = v.closesAt ? countdown(v.closesAt, now) : null;
        const of = v.closesAtBallots ?? (eligible && eligible >= total ? eligible : null);
        return (
          <article className="card decide" key={v.id} aria-label={v.question}>
            <p className="kicker muted small">{ca.sessionNow.decide}</p>
            <h3>{v.question}</h3>
            <p className="muted small">
              {canDirect && <>{v.key} · </>}
              {of ? ca.sessionNow.votedOf(total, of) : ca.live.voted(total, null)}
              {left && (
                <>
                  {' · '}
                  <span className="num">{ca.live.closesIn(left)}</span>
                </>
              )}
            </p>
            <ul className="plain options">
              {v.options.map((o) => {
                const count = v.counts[o] ?? 0;
                const pct = total ? Math.round((count / total) * 100) : 0;
                const mine = v.myBallot === o;
                return (
                  <li key={o}>
                    <button
                      type="button"
                      className={`option ${mine ? 'mine' : ''}`}
                      aria-pressed={mine}
                      // A visitor sees the tally; voting needs an account.
                      disabled={!me.authenticated}
                      onClick={() => {
                        if (!mine) void run(castBallot(code, v.id, o));
                      }}
                    >
                      <span className="option-row">
                        <span className="grow">
                          {o}
                          {mine && <span className="muted small"> ({ca.voteYours})</span>}
                        </span>
                        <span className="num">{count}</span>
                      </span>
                      <span className="tallybar">
                        <span style={{ width: `${pct}%` }} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {!me.authenticated ? (
              <Link className="button" to={`/signin?next=${encodeURIComponent(`/s/${code}`)}`}>
                {ca.live.signInToVote}
              </Link>
            ) : (
              v.myBallot && <p className="muted small">{ca.sessionNow.voteChange}</p>
            )}
            {canDirect && (
              <button type="button" onClick={() => void run(closeVote(code, v.id))}>
                {ca.voteClose}
              </button>
            )}
          </article>
        );
      })}
      {closed.length > 0 && !live && (
        <ul className="activity" aria-label={ca.live.closedVotes}>
          {closed.map((v) => (
            <li key={v.id}>
              <strong>{v.question}</strong>{' '}
              <span className="chip">{v.result ?? ca.voteClosed}</span>
            </li>
          ))}
        </ul>
      )}
      {canDirect && (
        <details className="card newvote">
          <summary>{ca.newVote}</summary>
          <form className="form" onSubmit={submit} aria-label={ca.newVote}>
            <label>
              {ca.voteKey}
              <input value={key} onChange={(e) => setKey(e.target.value)} />
            </label>
            <label>
              {ca.voteQuestion}
              <input value={question} onChange={(e) => setQuestion(e.target.value)} required />
            </label>
            <label>
              {ca.voteOptions}
              <textarea
                value={options}
                onChange={(e) => setOptions(e.target.value)}
                rows={3}
                required
              />
            </label>
            <label>
              {ca.voteClosesIn}
              <input
                type="number"
                min={1}
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
              />
            </label>
            <label>
              {ca.live.quorumLabel}
              <input
                type="number"
                min={1}
                value={quorum}
                onChange={(e) => setQuorum(e.target.value)}
              />
            </label>
            <button type="submit" className="primary">
              {ca.openVote}
            </button>
          </form>
        </details>
      )}
    </section>
  );
}

/** Colour is the threshold, not the decoration: done, failed, and the one being worked on. */
const STATE_CHIP: Record<string, string> = {
  done: 'ok',
  failed: 'bad',
  active: 'warn',
};

/** The form that changes an objective in place: title, type, priority, radius, nothing else. */
function ObjectiveEditForm({
  objective,
  onSave,
  onClose,
}: {
  objective: Objective;
  onSave: (patch: ObjectivePatch) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(objective.title ?? '');
  const [type, setType] = useState(objective.type);
  const [priority, setPriority] = useState(objective.priority ?? 'main');
  const [radius, setRadius] = useState(String(objective.radius ?? 250));
  return (
    <form
      className="form objective-edit"
      aria-label={`${ca.objectiveUi.edit}: ${objective.title ?? objective.id}`}
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          title: title.trim(),
          type,
          priority,
          radius: Number(radius) || 250,
        });
        onClose();
      }}
    >
      <label>
        {ca.objectiveTitle}
        <input value={title} onChange={(e) => setTitle(e.target.value)} required />
      </label>
      <div className="two">
        <label>
          {ca.objectiveType}
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {Object.entries(ca.objectiveUi.types).map(([key, name]) => (
              <option key={key} value={key}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {ca.objectivePriority}
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as 'main' | 'secondary' | 'hidden')}
          >
            {(['main', 'secondary', 'hidden'] as const).map((p) => (
              <option key={p} value={p}>
                {ca.priorityNames[p]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label>
        {ca.objectiveRadius}
        <input
          type="number"
          min={50}
          value={radius}
          onChange={(e) => setRadius(e.target.value)}
          required
        />
      </label>
      <p className="muted small">{ca.objectiveUi.editHint}</p>
      <div className="row actions">
        <button type="submit" className="primary small">
          {ca.objectiveUi.save}
        </button>
        <button type="button" className="small ghost" onClick={onClose}>
          {ca.objectiveUi.cancelEdit}
        </button>
      </div>
    </form>
  );
}

/**
 * The objectives card of the session and the direction pages: the list beside the map and
 * tied to it both ways (picking a row lights its marker, picking a marker opens its row),
 * with the type as an icon, the state as a chip, and inline editing for whoever the
 * platform says may edit the mission. The actions a page passes are the actions it already
 * had: reveal and cancel belong to the direction page, and only there.
 */
export function ObjectivesCard({
  objectives,
  states,
  titleOf,
  selected,
  onSelect,
  canEdit,
  onEdit,
  onReveal,
  onCancel,
  children,
}: {
  objectives: Objective[];
  states: Map<string, { state: string; title: string }>;
  titleOf: (o: Objective) => string;
  selected: string | null;
  onSelect: (id: string) => void;
  /** Whether this objective can be changed here at all: the mission's permission, not the session's. */
  canEdit?: (id: string) => boolean;
  onEdit?: (id: string, patch: ObjectivePatch) => void;
  onReveal?: (o: Objective) => void;
  onCancel?: (o: Objective) => void;
  /** The direction page's "add an objective" form, under the heading. */
  children?: ReactNode;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  return (
    <article className="card objectives" aria-label={ca.objectives}>
      <h2>{ca.objectives}</h2>
      {children}
      {objectives.length === 0 && <p className="muted small">{ca.objectiveUi.none}</p>}
      <ol className="plain objective-list">
        {objectives.map((o, i) => {
          const seen = states.get(o.id);
          const state = seen?.state ?? 'pending';
          const editable = !!onEdit && (canEdit?.(o.id) ?? false);
          return (
            <li
              key={o.id}
              className={`objective-row ${o.priority ?? 'main'} ${selected === o.id ? 'selected' : ''}`}
            >
              <div className="row">
                <button
                  type="button"
                  className="objective-pick grow"
                  aria-pressed={selected === o.id}
                  title={ca.objectiveUi.select}
                  onClick={() => onSelect(o.id)}
                >
                  <span className="objective-no num">{i + 1}</span>
                  <i
                    className={`bi bi-${OBJECTIVE_ICONS[o.type] ?? 'geo-alt'}`}
                    aria-hidden="true"
                  />
                  <span className="objective-name">
                    <strong>{titleOf(o)}</strong>
                    <span className="muted small">
                      {ca.objectiveUi.types[o.type] ?? o.type} ·{' '}
                      {ca.priorityNames[o.priority ?? 'main']}
                    </span>
                  </span>
                </button>
                <span className={`chip ${STATE_CHIP[state] ?? ''}`}>
                  {ca.objectiveUi.states[state] ?? state}
                </span>
              </div>
              <div className="row objective-actions">
                {onReveal && o.priority === 'hidden' && !seen && (
                  <button type="button" onClick={() => onReveal(o)}>
                    {ca.reveal}
                  </button>
                )}
                {onCancel && !['done', 'failed', 'cancelled'].includes(state) && (
                  <button type="button" className="small" onClick={() => onCancel(o)}>
                    {ca.cancel}
                  </button>
                )}
                {editable && (
                  <button
                    type="button"
                    className="small ghost"
                    aria-expanded={editing === o.id}
                    onClick={() => setEditing(editing === o.id ? null : o.id)}
                  >
                    <i className="bi bi-pencil" aria-hidden="true" />
                    {ca.edit}
                  </button>
                )}
                {onReveal && <PlannedChip id="objective-assign-extend" named />}
              </div>
              {editable && editing === o.id && (
                <ObjectiveEditForm
                  objective={o}
                  onSave={(patch) => onEdit?.(o.id, patch)}
                  onClose={() => setEditing(null)}
                />
              )}
            </li>
          );
        })}
      </ol>
    </article>
  );
}

/** Copies one value and says so; there is no clipboard in some browsers, and none in tests. */
function CopyButton({ label, value }: { label: string; value: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="small"
      onClick={() =>
        void navigator.clipboard
          ?.writeText(value)
          .then(() => setDone(true))
          .catch(() => undefined)
      }
    >
      <i className={`bi bi-${done ? 'clipboard-check' : 'clipboard'}`} aria-hidden="true" />
      {done ? ca.join.copied : label}
    </button>
  );
}

/**
 * How to get into the server (plan 3.13): the address, the port and the players' password,
 * each with a copy button, and the connect link as the one primary action. The password
 * here is the players' one; the admin password never leaves the panel.
 */
export function JoinCard({
  server,
  when,
  card,
}: {
  server: SessionFull['server'];
  /** The session's time, on a page whose session has not started yet. */
  when?: string | null;
  /** The test card's title, on a card's page: two cards' servers can run at once and look alike. */
  card?: string | null;
}) {
  const address = server && server.host ? `${server.host}:${server.port}` : null;
  return (
    <article className="card join" aria-label={ca.join.title}>
      <h2>
        <i className="bi bi-door-open" aria-hidden="true" /> {ca.join.title}
      </h2>
      <dl className="facts">
        {card && (
          <div>
            <dt>{ca.join.card}</dt>
            <dd>{card}</dd>
          </div>
        )}
        {when && (
          <div>
            <dt>{ca.live.when}</dt>
            <dd>{when}</dd>
          </div>
        )}
        {server && (
          <div>
            <dt>{ca.join.server}</dt>
            <dd>{server.name}</dd>
          </div>
        )}
        {server && (
          <div>
            <dt>{ca.join.address}</dt>
            <dd className="num">{address ?? ca.live.serverAddressUnknown}</dd>
          </div>
        )}
        {server && server.password ? (
          <div>
            <dt>{ca.join.password}</dt>
            <dd className="num">{server.password}</dd>
          </div>
        ) : null}
      </dl>
      {!server && <p className="muted small">{ca.live.serverNone}</p>}
      {server && !address && (
        <>
          <p className="muted small">{ca.join.addressUnknown}</p>
          <p className="muted small">{ca.join.addressUnknownHint}</p>
        </>
      )}
      <div className="row actions">
        {server?.connectUrl && (
          <a className="button primary" href={server.connectUrl}>
            <i className="bi bi-play-fill" aria-hidden="true" />
            {ca.live.joinNow}
          </a>
        )}
        {address && <CopyButton label={ca.join.copyAddress} value={address} />}
        {server?.password && <CopyButton label={ca.join.copyPassword} value={server.password} />}
      </div>
      {server?.connectUrl && <p className="muted small">{ca.join.steamHint}</p>}
    </article>
  );
}
